import { GenerateTranslationsDialog } from '@/components/shared/GenerateTranslationsDialog';
import {
  createAttractionThemeTranslationsConfig,
  type AttractionThemeTranslation,
} from '@/config/objects/attraction-theme-translations';
import { useGenerateTranslations } from '@/hooks/useGenerateTranslations';
import { CrudListView } from '@startupquickstart/frontend';
import { Languages, Loader2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface AttractionThemeTranslationsListProps {
  attractionId: string;
  themeId: string;
  className?: string;
}

/**
 * Displays all translations for a theme using standard CrudListView
 */
export function AttractionThemeTranslationsList({
  attractionId,
  themeId,
  className,
}: AttractionThemeTranslationsListProps) {
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const generateMutation = useGenerateTranslations();
  const navigate = useNavigate();

  const handleGenerateTranslations = useCallback(() => {
    setGenerateDialogOpen(true);
  }, []);

  const translationsConfig = useMemo(
    () =>
      createAttractionThemeTranslationsConfig(attractionId, themeId, {
        onGenerateTranslations: handleGenerateTranslations,
      }),
    [attractionId, themeId, handleGenerateTranslations],
  );

  const handleItemClick = useCallback(
    (translation: AttractionThemeTranslation) => {
      navigate(`/attractions/${attractionId}/themes/${themeId}/translations/${translation.id}`);
    },
    [attractionId, themeId, navigate],
  );

  return (
    <>
      <div className={className}>
        <CrudListView
          config={translationsConfig}
          baseUrl={`/attractions/${attractionId}/themes/${themeId}/translations`}
          onItemClick={handleItemClick}
          getValueFromItem={(translation: AttractionThemeTranslation) => translation.id}
          getLabelFromItem={(translation: AttractionThemeTranslation) =>
            `${translation.language?.name || 'Unknown'} - ${translation.name}`
          }
          headerActions={[
            {
              key: 'generate',
              label: 'Generate Translations',
              icon: generateMutation.isPending ? Loader2 : Languages,
              variant: 'default' as const,
              onClick: () => setGenerateDialogOpen(true),
              loading: generateMutation.isPending,
            },
          ]}
        />
      </div>

      <GenerateTranslationsDialog
        open={generateDialogOpen}
        onClose={() => {
          setGenerateDialogOpen(false);
        }}
        title="Generate Translations"
        description="Select languages to translate this theme to. Existing translations will be skipped."
        onGenerate={selectedLanguageIds => {
          generateMutation.mutate(
            {
              attractionId,
              themeId,
              targetLanguageIds: selectedLanguageIds,
            },
            {
              onSuccess: () => {
                setGenerateDialogOpen(false);
              },
            },
          );
        }}
        isGenerating={generateMutation.isPending}
      />
    </>
  );
}
